'use client'
import { logger } from '@/lib/logger';

import React, { memo } from 'react'
import { Button } from '../ui/button'
import ToggleSwitch from '../ui/toggle-switch'
import { SettingsState } from '@/types/settings'

interface AdvancedSettingsPanelProps {
  settings: SettingsState
  onSettingChange: (key: keyof SettingsState, value: any) => void
  onResetSection: (section: string) => void
  onResetAll: () => void
  onExport: () => void
  onImport: (settings: SettingsState) => void
}

const AdvancedSettingsPanel = memo<AdvancedSettingsPanelProps>(({
  settings,
  onSettingChange,
  onResetSection,
  onResetAll,
  onExport,
  onImport
}) => {
  return (
    <div className="space-y-6">
      {/* SYSTEM DIAGNOSTICS */}
      <div className="p-4 bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-lg border border-red-600/30">
        <h4 className="text-sm font-medium text-red-400 mb-3">⚠️ Advanced System Controls</h4>
        <p className="text-xs text-red-300 mb-3">These settings are for experienced users only</p>
        <div className="flex space-x-2">
          <Button
            onClick={() => onResetSection('advanced')}
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs border-red-600/50 text-red-400 hover:bg-red-600/10"
          >
            Reset Advanced
          </Button>
        </div>
      </div>

      {/* Log Level - DEBUGGING POWER */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-200" htmlFor="logLevel">
          Log Level <span className="text-yellow-400 uppercase">({settings.logLevel})</span>
        </label>
        <select
          id="logLevel"
          value={settings.logLevel}
          onChange={(e) => onSettingChange('logLevel', e.target.value)}
          className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-slate-200 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/30"
          aria-label="Select log level"
        >
          <option value="error">🔴 Error Only (Production)</option>
          <option value="warn">🟡 Warnings (Development)</option>
          <option value="info">🔵 Info (Default)</option>
          <option value="debug">🟢 Debug (Verbose)</option>
          <option value="trace">🟣 Trace (Maximum)</option>
        </select>
      </div>

      {/* POWER TOGGLES - ADVANCED FEATURES */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Debug Mode Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Debug Mode</h4>
            <p className="text-xs text-slate-400">Console logging</p>
          </div>
          <ToggleSwitch
            value={settings.enableDebug}
            onChange={(value) => onSettingChange('enableDebug', value)}
            activeColor="red"
            ariaLabel={settings.enableDebug ? "Disable debug mode" : "Enable debug mode"}
          />
        </div>

        {/* Experimental Features Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Experimental</h4>
            <p className="text-xs text-slate-400">Beta features</p>
          </div>
          <ToggleSwitch
            value={settings.enableExperimental}
            onChange={(value) => onSettingChange('enableExperimental', value)}
            activeColor="purple"
            ariaLabel={settings.enableExperimental ? "Disable experimental features" : "Enable experimental features"}
          />
        </div>

        {/* Developer Mode Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Developer Mode</h4>
            <p className="text-xs text-slate-400">Advanced tools</p>
          </div>
          <ToggleSwitch
            value={settings.developerMode}
            onChange={(value) => onSettingChange('developerMode', value)}
            activeColor="orange"
            ariaLabel={settings.developerMode ? "Disable developer mode" : "Enable developer mode"}
          />
        </div>

        {/* Telemetry Toggle */}
        <div className="flex items-center justify-between p-6 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Telemetry</h4>
            <p className="text-xs text-slate-400">Usage analytics</p>
          </div>
          <ToggleSwitch
            value={settings.enableTelemetry}
            onChange={(value) => onSettingChange('enableTelemetry', value)}
            activeColor="blue"
            ariaLabel={settings.enableTelemetry ? "Disable telemetry" : "Enable telemetry"}
          />
        </div>
      </div>

      {/* EXPORT/IMPORT CONTROLS */}
      <div className="p-4 bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-lg border border-purple-600/30">
        <h4 className="text-sm font-medium text-purple-400 mb-3">💾 Settings Management</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Button
            onClick={onExport}
            className="h-8 px-3 text-xs bg-purple-600/20 border border-purple-600/50 text-purple-300 hover:bg-purple-600/30"
          >
            📤 Export
          </Button>
          <Button
            onClick={() => {
              const input = document.createElement('input')
              input.type = 'file'
              input.accept = '.json'
              input.onchange = (e) => {
                const file = (e.target as HTMLInputElement).files?.[0]
                if (file) {
                  const reader = new FileReader()
                  reader.onload = (e) => {
                    try {
                      const importedSettings = JSON.parse(e.target?.result as string)
                      onImport(importedSettings)
                    } catch (error) {
                      logger.error('Failed to import settings:', error)
                    }
                  }
                  reader.readAsText(file)
                }
              }
              input.click()
            }}
            className="h-8 px-3 text-xs bg-blue-600/20 border border-blue-600/50 text-blue-300 hover:bg-blue-600/30"
          >
            📥 Import
          </Button>
          <Button
            onClick={onResetAll}
            className="h-8 px-3 text-xs bg-red-600/20 border border-red-600/50 text-red-300 hover:bg-red-600/30"
          >
            🔄 Reset All
          </Button>
        </div>
      </div>

      {/* SYSTEM INFORMATION */}
      <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-600/30">
        <h4 className="text-sm font-medium text-slate-200 mb-3">🖥️ System Information</h4>
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <span className="text-slate-400">User Agent:</span>
            <div className="text-slate-300 font-mono text-xs leading-relaxed break-all">{navigator.userAgent}</div>
          </div>
          <div>
            <span className="text-slate-400">Platform:</span>
            <div className="text-slate-300 font-mono">{navigator.platform}</div>
          </div>
          <div>
            <span className="text-slate-400">Language:</span>
            <div className="text-slate-300 font-mono">{navigator.language}</div>
          </div>
          <div>
            <span className="text-slate-400">Cores:</span>
            <div className="text-slate-300 font-mono">{navigator.hardwareConcurrency || 'Unknown'}</div>
          </div>
        </div>
      </div>
    </div>
  )
})

AdvancedSettingsPanel.displayName = 'AdvancedSettingsPanel'

export default AdvancedSettingsPanel